import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { LuSearch, LuX, LuZapOff, LuLocateFixed } from 'react-icons/lu';

import Page from '../../components/ui/Page/Page';
import PageHeader from '../../components/ui/PageHeader/PageHeader';
import TextField from '../../components/ui/TextField/TextField';
import Button from '../../components/ui/Button/Button';
import EmptyState from '../../components/ui/EmptyState/EmptyState';
import ErrorState from '../../components/ui/ErrorState/ErrorState';
import { SkeletonCard } from '../../components/ui/LoadingSkeleton/LoadingSkeleton';

import usePublishedStations from './hooks/usePublishedStations.js';
import useGeolocation from './hooks/useGeolocation.js';
import useDebouncedValue from '../../hooks/useDebouncedValue.js';

import { haversineKm, isValidCoord, formatKm } from './utils/distance.js';
import { findNearestCharger, stationKey, geolocationErrorMessage } from './utils/nearestCharger.js';
import { normaliseLat, normaliseLng } from './utils/coords.js';
import { filterAndRank } from './utils/stationSearch.js';
import {
  classifyStationAvailability,
  AVAILABILITY,
  stationStatusDisplay,
} from './utils/stationAvailability.js';

import { chargerReturnState } from '../../utils/chargerNav.js';
import ChargerCard from './components/ChargerCard.jsx';
import ChargerNetworkMap from './components/ChargerNetworkMap.jsx';
import Pagination from './components/Pagination.jsx';
import ScanChargerButton from './components/ScanChargerButton.jsx';
import './FindChargerPage.less';

const PAGE_SIZE = 10;
// The browser's own timeout does not start until the permission prompt is
// answered, so a prompt left open would leave the button busy forever.
const GEO_OPTIONS = { enableHighAccuracy: false, timeout: 10000, maximumAge: 60_000 };
const GEO_SAFETY_MS = 30_000;

function normaliseStation(raw) {
  // Backend serves `verified_stations.lat` / `.lng` as PostgreSQL
  // NUMERIC, which node-postgres returns as JavaScript strings by
  // default. The previous `typeof raw.lat === 'number'` guard
  // silently dropped every real charger's coordinates, which in
  // turn dropped the Get-directions action from every row. Route
  // both `lat`/`lng` and the `latitude`/`longitude` aliases the
  // map utility already tolerates through the shared
  // `normaliseLat` / `normaliseLng` helpers so the map and the
  // list agree on what "has coordinates" means.
  const lat = normaliseLat(raw.lat ?? raw.latitude);
  const lng = normaliseLng(raw.lng ?? raw.longitude);
  // Maintenance mode takes display priority over Available / In-use so
  // the card label and pin colour never contradict the admin's override.
  const display = stationStatusDisplay(raw);
  return {
    stationId: raw.station_id,
    chargerCode: raw.public_id || null,
    displayName: raw.display_name || raw.public_id || raw.station_id,
    locationText: raw.location_text || '',
    // Prefer the API-derived resolvable URL (canonical GCS URL under
    // the GCS driver). Photo name is kept as the fallback for the
    // client-side resolver — see components/StationPhoto/StationPhoto
    // and utils/stationPhotos.
    photoUrl: raw.photo_url || null,
    photoName: raw.photo_name || null,
    lat,
    lng,
    connectorType: raw.connector_type || '',
    powerKw:
      typeof raw.max_power_kw === 'number'
        ? raw.max_power_kw
        : raw.max_power_kw
        ? Number(raw.max_power_kw)
        : null,
    availability: classifyStationAvailability(raw),
    maintenanceMode: raw.maintenance_mode === true,
    // Raw OCPP connector status passthrough — the map's marker
    // resolver uses it to distinguish a Faulted charger (red pin)
    // from the generic UNAVAILABLE bucket (slate pin). Left as
    // `null` when the API row doesn't carry one so the resolver
    // stays in its safe fallback path.
    connectorStatus: raw.connector_status || null,
    statusLabel: display.label,
    statusTone: display.tone,
    availableConnectors:
      typeof raw.available_connectors === 'number'
        ? raw.available_connectors
        : raw.available_connectors != null
        ? Number(raw.available_connectors)
        : null,
    totalConnectors:
      typeof raw.total_connectors === 'number'
        ? raw.total_connectors
        : raw.total_connectors != null
        ? Number(raw.total_connectors)
        : null,
  };
}

function sortByDistanceThenName(stations) {
  return [...stations].sort((a, b) => {
    const aHas = Number.isFinite(a.distanceKm);
    const bHas = Number.isFinite(b.distanceKm);
    if (aHas && !bHas) return -1;
    if (!aHas && bHas) return 1;
    if (aHas && bHas && a.distanceKm !== b.distanceKm) {
      return a.distanceKm - b.distanceKm;
    }
    const aAvail = a.availability === AVAILABILITY.AVAILABLE ? 0 : 1;
    const bAvail = b.availability === AVAILABILITY.AVAILABLE ? 0 : 1;
    if (aAvail !== bAvail) return aAvail - bAvail;
    return String(a.displayName || '').localeCompare(
      String(b.displayName || ''),
    );
  });
}

function sortByName(stations) {
  return [...stations].sort((a, b) => {
    const aAvail = a.availability === AVAILABILITY.AVAILABLE ? 0 : 1;
    const bAvail = b.availability === AVAILABILITY.AVAILABLE ? 0 : 1;
    if (aAvail !== bAvail) return aAvail - bAvail;
    return String(a.displayName || '').localeCompare(
      String(b.displayName || ''),
    );
  });
}

export default function FindChargerPage() {
  const navigate = useNavigate();
  const { stations: raw, loading, error, refetch } = usePublishedStations();
  const { coords: autoCoords, denied, unsupported } = useGeolocation();
  // A position the driver asked for with "Find nearest charger" wins over the
  // passive one from page load (which may have been denied or be stale).
  const [manualCoords, setManualCoords] = useState(null);
  const coords = manualCoords || autoCoords;

  const [query, setQuery] = useState('');
  const [page, setPage] = useState(1);
  const debouncedQuery = useDebouncedValue(query, 150);

  const searchRef = useRef(null);
  const geoTimer = useRef(null);
  const [locating, setLocating] = useState(false);
  const [nearestNote, setNearestNote] = useState(null); // { kind: 'ok' | 'info' | 'error', text }
  const [highlightKey, setHighlightKey] = useState(null);
  const [pendingFocus, setPendingFocus] = useState(null);

  const normalised = useMemo(() => {
    if (!Array.isArray(raw)) return [];
    return raw.map(normaliseStation);
  }, [raw]);

  const withDistance = useMemo(() => {
    if (!coords || !isValidCoord(coords.lat) || !isValidCoord(coords.lng)) {
      return normalised;
    }
    return normalised.map((s) => {
      if (!isValidCoord(s.lat) || !isValidCoord(s.lng)) return s;
      const distanceKm = haversineKm(coords, { lat: s.lat, lng: s.lng });
      return { ...s, distanceKm };
    });
  }, [normalised, coords]);

  const { matches, hasQuery } = useMemo(
    () => filterAndRank(withDistance, debouncedQuery),
    [withDistance, debouncedQuery],
  );

  const sorted = useMemo(() => {
    if (hasQuery) return matches;
    if (coords) return sortByDistanceThenName(matches);
    return sortByName(matches);
  }, [matches, hasQuery, coords]);

  // Reset to page 1 whenever the effective search changes. Also reset
  // when the sort key would change (query cleared, coords arrive), so
  // the paged view always starts at the top of the fresh ordering.
  useEffect(() => {
    setPage(1);
  }, [debouncedQuery, coords, raw]);

  // Clamp page to the current total when results shrink.
  const totalPages = Math.max(1, Math.ceil(sorted.length / PAGE_SIZE));
  useEffect(() => {
    if (page > totalPages) setPage(totalPages);
  }, [page, totalPages]);

  const paged = useMemo(
    () => sorted.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE),
    [sorted, page],
  );

  const resultsHeadingRef = useRef(null);

  const handlePageChange = useCallback((next) => {
    setPage(next);
    // Focus the results heading so keyboard users land at the top of
    // the new page; also scrolls the results into view for pointer
    // users. Fires after paint so the new list is present.
    requestAnimationFrame(() => {
      const el = resultsHeadingRef.current;
      if (!el) return;
      el.focus({ preventScroll: false });
      // `scrollIntoView` is missing under jsdom; guard so the callback
      // is still safe in tests without changing prod behaviour.
      if (typeof el.scrollIntoView === 'function') {
        el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    });
  }, []);

  // Bring the chosen charger into view: right list page, scrolled to, focused.
  useEffect(() => {
    if (!pendingFocus || hasQuery) return;
    const idx = sorted.findIndex((s) => stationKey(s) === pendingFocus);
    if (idx < 0) return;
    const targetPage = Math.floor(idx / PAGE_SIZE) + 1;
    if (page !== targetPage) {
      setPage(targetPage);
      return;
    }
    setPendingFocus(null);
    requestAnimationFrame(() => {
      const card = Array.from(document.querySelectorAll('[data-station-key]'))
        .find((n) => n.getAttribute('data-station-key') === pendingFocus);
      if (!card) return;
      const target = card.querySelector('.gc-charger-row-hitarea') || card;
      target.focus({ preventScroll: true });
      if (typeof card.scrollIntoView === 'function') {
        const reduce = typeof window.matchMedia === 'function'
          && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        card.scrollIntoView({ behavior: reduce ? 'auto' : 'smooth', block: 'center' });
      }
    });
  }, [pendingFocus, hasQuery, sorted, page]);

  useEffect(() => () => clearTimeout(geoTimer.current), []);

  const failNearest = useCallback((reason) => {
    setNearestNote({
      kind: 'error',
      text: `${geolocationErrorMessage(reason)} You can search by charger code, station or location instead.`,
    });
    // Fallback path: hand the driver the manual search.
    searchRef.current?.focus();
  }, []);

  const handleFindNearest = useCallback(() => {
    if (locating) return;
    setNearestNote(null);
    if (typeof navigator === 'undefined' || !navigator.geolocation) {
      failNearest('unsupported');
      return;
    }
    setLocating(true);
    let settled = false;
    const settle = (fn) => {
      if (settled) return;
      settled = true;
      clearTimeout(geoTimer.current);
      setLocating(false);
      fn();
    };
    geoTimer.current = setTimeout(() => settle(() => failNearest('timeout')), GEO_SAFETY_MS);
    navigator.geolocation.getCurrentPosition(
      (pos) => settle(() => {
        const origin = { lat: pos?.coords?.latitude, lng: pos?.coords?.longitude };
        if (!isValidCoord(origin.lat) || !isValidCoord(origin.lng)) {
          failNearest(undefined);
          return;
        }
        const result = findNearestCharger(normalised, origin);
        setManualCoords(origin);
        if (!result) {
          setNearestNote({
            kind: 'error',
            text: 'No chargers have a known location to compare. You can search by charger code, station or location instead.',
          });
          searchRef.current?.focus();
          return;
        }
        const { station, distanceKm, usable } = result;
        const name = station.displayName || station.chargerCode || 'Charger';
        const away = formatKm(distanceKm);
        setNearestNote(usable
          ? { kind: 'ok', text: `Nearest available charger: ${name}${away ? `, ${away} away` : ''}.` }
          : {
            kind: 'info',
            text: `No chargers are available right now. The nearest is ${name} (${station.statusLabel || 'status unknown'})${away ? `, ${away} away` : ''}.`,
          });
        setQuery('');
        setHighlightKey(stationKey(station));
        setPendingFocus(stationKey(station));
      }),
      (err) => settle(() => failNearest(err)),
      GEO_OPTIONS,
    );
  }, [locating, normalised, failNearest]);

  const openCharger = useCallback(
    (station) => {
      const state = chargerReturnState({
        returnTo: '/charge',
        source: 'user-dashboard',
      });
      if (station.chargerCode) {
        navigate(`/c/${encodeURIComponent(station.chargerCode)}`, { state });
        return;
      }
      if (station.stationId) {
        navigate(
          `/station?stationId=${encodeURIComponent(station.stationId)}`,
          { state },
        );
      }
    },
    [navigate],
  );

  const sectionTitle = hasQuery
    ? 'Search results'
    : coords
    ? 'Chargers near you'
    : 'All chargers';

  const showLocationNote =
    !hasQuery && !coords && (denied || unsupported);

  const trimmedQuery = query.trim();

  return (
    <Page>
      <PageHeader
        title="Find a charger"
        subtitle="Find a charger near you or search by charger code, station, or location."
        action={
          <Button
            variant="secondary"
            className="gc-find-nearest"
            leadingIcon={<LuLocateFixed />}
            busy={locating}
            onClick={handleFindNearest}
          >
            {locating ? 'Locating…' : 'Find nearest charger'}
          </Button>
        }
      />

      <div className="gc-find-controls">
        <div className="gc-find-search-row">
          <div className="gc-find-search">
            <span className="gc-find-search-icon" aria-hidden="true">
              <LuSearch />
            </span>
            <TextField
              label="Search chargers"
              name="chargerSearch"
              ref={searchRef}
              value={query}
              onChange={(e) => { setQuery(e.target.value); setHighlightKey(null); }}
              autoComplete="off"
              placeholder="Search by charger code, station, or location"
            />
            {trimmedQuery && (
              <button
                type="button"
                className="gc-find-search-clear"
                onClick={() => setQuery('')}
                aria-label="Clear search"
              >
                <LuX aria-hidden="true" />
              </button>
            )}
          </div>
          {/*
            Scan button only renders on native (iOS / Android). On web
            the search field takes the full row width as before.
          */}
          <ScanChargerButton />
        </div>

        {/* Always mounted so screen readers announce the result when it is set. */}
        <p
          className={`gc-find-nearest-note${nearestNote && nearestNote.kind !== 'error' ? ` is-${nearestNote.kind}` : ''}`}
          role="status"
          aria-live="polite"
        >
          {nearestNote && nearestNote.kind !== 'error' ? nearestNote.text : ''}
        </p>
        {nearestNote?.kind === 'error' && (
          <p className="gc-find-nearest-note is-error" role="alert">{nearestNote.text}</p>
        )}

        {showLocationNote && (
          <p className="gc-find-location-note" role="note">
            <LuZapOff aria-hidden="true" />
            <span>Location is unavailable. Showing all chargers.</span>
          </p>
        )}
      </div>

      <ChargerNetworkMap chargers={sorted} onOpenCharger={openCharger} selectedId={highlightKey} />

      <section className="gc-find-section" aria-labelledby="gc-find-results-heading">
        <div className="gc-find-toolbar">
          <h2
            id="gc-find-results-heading"
            className="gc-find-toolbar-title"
            ref={resultsHeadingRef}
            tabIndex={-1}
          >
            {sectionTitle}
          </h2>
          {!loading && !error && (
            <span className="gc-find-toolbar-count" aria-live="polite">
              {sorted.length} {sorted.length === 1 ? 'charger' : 'chargers'}
            </span>
          )}
        </div>

        {loading && (
          <div className="gc-find-list" aria-busy="true" aria-live="polite">
            <SkeletonCard />
            <SkeletonCard />
            <SkeletonCard />
          </div>
        )}

        {!loading && error && (
          <ErrorState
            title="Couldn't load chargers"
            description="Check your connection and try again."
            onRetry={refetch}
          />
        )}

        {!loading && !error && sorted.length === 0 && !hasQuery && (
          <EmptyState
            title="No chargers available"
            description="No chargers are currently available."
          />
        )}

        {!loading && !error && sorted.length === 0 && hasQuery && (
          <EmptyState
            title="No results"
            description={`No chargers found for "${debouncedQuery.trim()}".`}
          />
        )}

        {!loading && !error && paged.length > 0 && (
          <>
            <div className="gc-find-list">
              {paged.map((station) => (
                <ChargerCard
                  key={station.stationId || station.chargerCode}
                  station={station}
                  highlighted={highlightKey != null && stationKey(station) === highlightKey}
                  onSelect={() => openCharger(station)}
                />
              ))}
            </div>
            <Pagination
              page={page}
              totalPages={totalPages}
              onChange={handlePageChange}
              ariaLabel="Charger list pagination"
            />
          </>
        )}
      </section>
    </Page>
  );
}
